import resolveServiceWorkerContainer from './resolveServiceWorkerContainer';

export type ServiceWorkerUpdateClock = {
	readonly now: () => number;
};

export type WatchServiceWorkerUpdateOptions = {
	readonly container?: ServiceWorkerContainer;
	readonly visibilityTarget?: Pick<
		Document,
		'addEventListener' | 'removeEventListener' | 'visibilityState'
	>;
	readonly clock?: ServiceWorkerUpdateClock;
	readonly minimumUpdateIntervalMs?: number;
	readonly onUpdateAvailable: (registration: ServiceWorkerRegistration) => void;
};

const DEFAULT_MINIMUM_UPDATE_INTERVAL_MS = 60 * 60 * 1000;

/**
 * Subscribes to the registration controlling this page and calls back once a new worker is
 * installed and waiting to activate. Also probes for an update on every return to the foreground,
 * throttled to a one-hour minimum interval (on top of the check the browser already runs on every
 * navigation) — the clock is injected because that interval is otherwise untestable. Returns an
 * unsubscribe function; a container-less browser returns a no-op one.
 */
const watchServiceWorkerUpdate = (options: WatchServiceWorkerUpdateOptions): (() => void) => {
	const container = options.container ?? resolveServiceWorkerContainer();
	const visibilityTarget = options.visibilityTarget ?? document;
	const clock = options.clock ?? { now: () => Date.now() };
	const minimumUpdateIntervalMs = options.minimumUpdateIntervalMs ?? DEFAULT_MINIMUM_UPDATE_INTERVAL_MS;
	const { onUpdateAvailable } = options;

	if (!container) {
		return () => {};
	}

	let watchedRegistration: ServiceWorkerRegistration | null = null;
	let lastUpdateCheckAt = clock.now();

	const notifyIfWaiting = (registration: ServiceWorkerRegistration): void => {
		if (registration.waiting) {
			onUpdateAvailable(registration);
		}
	};

	const handleUpdateFound = (registration: ServiceWorkerRegistration): void => {
		const installingWorker = registration.installing;
		if (!installingWorker) {
			return;
		}

		installingWorker.addEventListener('statechange', () => {
			if (installingWorker.state === 'installed') {
				notifyIfWaiting(registration);
			}
		});
	};

	const handleVisibilityChange = (): void => {
		if (visibilityTarget.visibilityState !== 'visible') {
			return;
		}

		if (!watchedRegistration) {
			return;
		}

		const now = clock.now();
		if (now - lastUpdateCheckAt < minimumUpdateIntervalMs) {
			return;
		}

		lastUpdateCheckAt = now;
		watchedRegistration.update().catch(() => {
			// A failed probe leaves the current worker serving the shell; it is not an application error.
		});
	};

	container.ready
		.then((registration) => {
			watchedRegistration = registration;
			notifyIfWaiting(registration);
			registration.addEventListener('updatefound', () => handleUpdateFound(registration));
		})
		.catch(() => {
			// No controlling registration yet (registration disabled or still pending): nothing to watch.
		});

	visibilityTarget.addEventListener('visibilitychange', handleVisibilityChange);

	return () => {
		visibilityTarget.removeEventListener('visibilitychange', handleVisibilityChange);
	};
};

export default watchServiceWorkerUpdate;
